import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCartesTrello, getListesTrello } from '../services/trelloService';
import { formatDate } from './devisUtils';
import ImportTrelloPanel from './ImportTrelloPanel';
import '../assets/styles/Parametrage.css';

/**
 * Copie un texte dans le presse-papiers.
 *
 * `navigator.clipboard` n'existe qu'en contexte sécurisé (HTTPS ou
 * localhost) : en production, servie en http sur une IP, l'application n'y
 * a pas droit. D'où le repli par `execCommand`, obsolète mais universel.
 */
async function copier(texte) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texte);
      return true;
    }
  } catch {
    /* refus du navigateur : on tente le repli ci-dessous */
  }
  try {
    const zone = document.createElement('textarea');
    zone.value = texte;
    zone.setAttribute('readonly', '');
    zone.style.position = 'fixed';
    zone.style.opacity = '0';
    document.body.appendChild(zone);
    zone.select();
    const ok = document.execCommand('copy');
    zone.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * Extrait lisible d'une description Trello, pour la vignette.
 *
 * Les descriptions viennent du formulaire du site et sont écrites en
 * Markdown : liens, gras, retours à la ligne. Sur deux lignes de vignette,
 * cette syntaxe ferait plus de bruit que de sens — on la retire de
 * L'APERÇU seulement. Le texte intégral, lui, s'affiche tel quel au
 * dépliage.
 */
function extrait(description) {
  return String(description || '')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Coche : la carte est déjà entrée comme demande de devis. */
function IconeImportee({ titre }) {
  return (
    <span className="trello-etat trello-etat-oui" title={titre} aria-label={titre} role="img">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/** Flèche entrante : la carte reste à importer. */
function IconeAImporter({ titre }) {
  return (
    <span className="trello-etat trello-etat-non" title={titre} aria-label={titre} role="img">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 4v11m0 0l-4-4m4 4l4-4M5 20h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/**
 * Demande de devis > Trello : les colonnes du tableau, telles que l'API les
 * renvoie.
 *
 * L'écran sert à lire l'identifiant d'une liste — celui que ID_LISTDEVIS
 * doit porter côté serveur pour que l'import des demandes pioche dans la
 * bonne colonne. La liste ainsi configurée est signalée.
 */
function ParametrageTrello() {
  const [donnees, setDonnees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtre, setFiltre] = useState('');
  const [copie, setCopie] = useState(null);

  // Liste dépliée : ses cartes sont chargées à la demande, pas toutes
  // d'avance — quatorze listes feraient quatorze appels pour rien.
  const [ouverte, setOuverte] = useState(null);
  const [cartes, setCartes] = useState([]);
  const [chargementCartes, setChargementCartes] = useState(false);
  const [erreurCartes, setErreurCartes] = useState(null);
  // Carte dépliée : une seule à la fois, le détail reste lisible.
  const [carteOuverte, setCarteOuverte] = useState(null);
  // Liste dont on veut importer les cartes comme demandes de devis.
  const [aImporter, setAImporter] = useState(null);
  // Carte seule à importer : le bouton de sa vignette.
  const [carteAImporter, setCarteAImporter] = useState(null);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    return getListesTrello()
      .then((res) => setDonnees(res.data))
      .catch((err) => setError(err.message || 'Impossible de joindre Trello.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    if (!copie) return undefined;
    const t = setTimeout(() => setCopie(null), 2000);
    return () => clearTimeout(t);
  }, [copie]);

  const listes = useMemo(() => (donnees ? donnees.listes : []), [donnees]);
  const visibles = useMemo(() => {
    const q = filtre.trim().toLowerCase();
    if (!q) return listes;
    return listes.filter((l) => l.nom.toLowerCase().includes(q) || l.id.includes(q));
  }, [listes, filtre]);

  function ouvrirListe(liste) {
    setOuverte(liste);
    setCartes([]);
    setCarteOuverte(null);
    setErreurCartes(null);
    setChargementCartes(true);
    getCartesTrello(liste.id)
      .then((res) => setCartes(res.data || []))
      .catch((err) => setErreurCartes(err.message || 'Cartes illisibles.'))
      .finally(() => setChargementCartes(false));
  }

  async function copierId(id) {
    setCopie((await copier(id)) ? id : 'echec');
  }

  return (
    <div className="param-page">
      <div className="param-header">
        <div>
          <h1 className="param-title">Trello</h1>
          <p className="param-subtitle">
            Colonnes du tableau Trello. La liste des demandes se règle via <code>ID_LISTDEVIS</code>{' '}
            côté serveur.
          </p>
        </div>
        <button type="button" className="param-btn" onClick={charger} disabled={loading}>
          {loading ? 'Chargement' : 'Rafraîchir'}
        </button>
      </div>

      <div className="param-toolbar">
        <div className="param-filtre">
          <input
            type="search"
            className="param-input"
            placeholder="Filtrer par nom ou identifiant"
            value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
          />
          {filtre && (
            <button type="button" className="param-filtre-clear" onClick={() => setFiltre('')}>
              Tout
            </button>
          )}
        </div>
        {donnees && (
          <span className="param-flash">
            Tableau {donnees.tableau} · {listes.length} liste{listes.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="param-scroll">
        {loading && (
          <div className="param-card">
            <div className="param-state">Interrogation de Trello...</div>
          </div>
        )}

        {!loading && error && (
          <div className="param-card">
            <div className="param-state param-state-error">{error}</div>
          </div>
        )}

        {!loading && !error && visibles.length === 0 && (
          <div className="param-card">
            <div className="param-state">
              {listes.length ? 'Aucune liste ne correspond.' : 'Ce tableau ne contient aucune liste.'}
            </div>
          </div>
        )}

        {!loading && !error && visibles.length > 0 && (
          <div className="param-card">
            <table className="param-table">
              <thead>
                <tr>
                  <th>LISTE</th>
                  <th>IMPORTÉES</th>
                  <th>IDENTIFIANT</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibles.map((l) => {
                  const estListeDevis = donnees.listeDevis === l.id;
                  return (
                    <tr
                      key={l.id}
                      className="param-row-clic"
                      onClick={() => ouvrirListe(l)}
                      title={`Voir les cartes de « ${l.nom.replace(/\s+/g, ' ')} »`}
                    >
                      <td>
                        {/* Un nom de liste Trello peut tenir sur plusieurs
                            lignes : on les conserve plutôt que de les aplatir. */}
                        <div style={{ whiteSpace: 'pre-line' }}>{l.nom}</div>
                        {estListeDevis && (
                          <span className="param-pill">Import des demandes</span>
                        )}
                      </td>
                      <td>
                        {/* Ce qui est déjà entré comme demande, sur ce que la
                            liste contient. Une liste vide n'affiche rien. */}
                        {l.cartes > 0 ? (
                          <>
                            <span
                              className={`param-pill trello-compteur${
                                l.importees === l.cartes
                                  ? ' trello-compteur-complet'
                                  : l.importees > 0
                                    ? ' trello-compteur-partiel'
                                    : ''
                              }`}
                            >
                              {l.importees} / {l.cartes}
                            </span>
                            {l.importees < l.cartes && (
                              <div className="devis-status-detail">
                                {l.cartes - l.importees} à importer
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="devis-status-detail">liste vide</span>
                        )}
                      </td>
                      <td>
                        <code>{l.id}</code>
                      </td>
                      {/* stopPropagation : copier un identifiant ne doit pas
                          ouvrir la liste par la même occasion. */}
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="param-btn param-btn-primary"
                            onClick={() => setAImporter(l)}
                            title="Importer les cartes de cette liste comme demandes de devis"
                          >
                            Importer
                          </button>
                          <button type="button" className="param-btn" onClick={() => copierId(l.id)}>
                            {copie === l.id ? 'Copié' : 'Copier l’id'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {copie === 'echec' && (
              <div className="param-form-message param-form-message-error">
                Copie refusée par le navigateur : sélectionnez l&apos;identifiant à la main.
              </div>
            )}
          </div>
        )}
      </div>

      {aImporter && (
        <ImportTrelloPanel
          liste={aImporter}
          onClose={() => setAImporter(null)}
          onImporte={() => {
            // Les compteurs « importées / total » et les icônes des cartes
            // ouvertes reflètent ce qui vient d'entrer.
            charger();
            if (ouverte) ouvrirListe(ouverte);
          }}
        />
      )}

      {ouverte && (
        <div className="param-modal-overlay" onClick={() => setOuverte(null)}>
          <div className="param-modal trello-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title" style={{ whiteSpace: 'pre-line' }}>
                {ouverte.nom}
              </h2>
              <button type="button" className="param-modal-close" onClick={() => setOuverte(null)}>
                ×
              </button>
            </div>

            <div className="param-modal-body trello-cartes">
              {chargementCartes && <div className="param-state">Chargement des cartes...</div>}
              {!chargementCartes && erreurCartes && (
                <div className="param-state param-state-error">{erreurCartes}</div>
              )}
              {!chargementCartes && !erreurCartes && cartes.length === 0 && (
                <div className="param-state">Cette liste ne contient aucune carte.</div>
              )}

              {!chargementCartes && !erreurCartes && cartes.length > 0 && (
                <>
                  <div className="trello-compte">
                    {cartes.length} carte{cartes.length > 1 ? 's' : ''} ·{' '}
                    {cartes.filter((c) => c.importee).length} déjà importée
                    {cartes.filter((c) => c.importee).length > 1 ? 's' : ''}
                  </div>
                  <div className="trello-grille">
                    {cartes.map((c) => {
                      const depliee = carteOuverte === c.id;
                      return (
                        <div
                          className={`trello-vignette${depliee ? ' trello-vignette-depliee' : ''}`}
                          key={c.id}
                          onClick={() => setCarteOuverte(depliee ? null : c.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setCarteOuverte(depliee ? null : c.id);
                            }
                          }}
                          aria-expanded={depliee}
                          title={depliee ? 'Replier' : 'Voir le détail'}
                        >
                          <div className="trello-vignette-tete">
                            {c.importee ? (
                              <IconeImportee titre="Déjà importée comme demande de devis" />
                            ) : (
                              <IconeAImporter titre="Pas encore importée" />
                            )}
                            <div className="trello-vignette-nom">{c.nom}</div>
                          </div>

                          <div className="trello-vignette-meta">
                            {c.etiquettes.slice(0, 2).map((e, i) => (
                              <span className="param-pill" key={i}>
                                {e.nom || e.couleur}
                              </span>
                            ))}
                            {c.echeance && (
                              <span className="param-pill">
                                {formatDate(c.echeance)}
                                {c.echeanceFaite ? ' ✓' : ''}
                              </span>
                            )}
                            <span>{formatDate(c.derniereActivite)}</span>
                          </div>

                          {c.description &&
                            (depliee ? (
                              /* Texte, jamais du HTML : React échappe, et une
                                 description Trello n'a pas à s'exécuter ici. */
                              <div className="trello-vignette-detail">{c.description}</div>
                            ) : (
                              <div className="trello-vignette-extrait">{extrait(c.description)}</div>
                            ))}

                          {/* stopPropagation : importer ne doit pas déplier
                              ou replier la vignette par la même occasion. */}
                          <div
                            className="trello-vignette-actions"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {c.importee ? (
                              <>
                                <span className="devis-status-detail">Déjà importée</span>
                                <button
                                  type="button"
                                  className="param-btn param-btn-sm"
                                  onClick={() => setCarteAImporter({ ...c, reimport: true })}
                                  title="Relire la carte dans Trello et reporter ses changements sur la demande"
                                >
                                  Re-importer
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="param-btn param-btn-sm param-btn-primary"
                                onClick={() => setCarteAImporter(c)}
                                title="Importer cette carte comme demande de devis"
                              >
                                Importer
                              </button>
                            )}
                            {c.lien && (
                              <a
                                className="trello-vignette-lien"
                                href={c.lien}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Ouvrir dans Trello
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Après la fenêtre de la liste dans le DOM : à z-index égal, c'est le
          dernier rendu qui passe au-dessus. Rendu avant, le panneau d'import
          d'une carte restait caché derrière la grille des cartes. */}
      {carteAImporter && (
        <ImportTrelloPanel
          carte={carteAImporter}
          reimport={Boolean(carteAImporter.reimport)}
          onClose={() => setCarteAImporter(null)}
          onImporte={() => {
            charger();
            if (ouverte) ouvrirListe(ouverte);
          }}
        />
      )}
    </div>
  );
}

export default ParametrageTrello;
